import React, { useState } from 'react';

const KNOWN_SUBJECTS = [
  "Accounting", "Afrikaans", "Biology", "Business Studies",
  "Chemistry", "English", "Geography", "History", "Life Sciences",
  "Mathematics", "Maths Literacy", "Physical Sciences", "isiXhosa", "Zulu",
  "Economics", "Information Technology"
];

const CAREER_DATA = [
  {
    career: "BSc Computer Science",
    requirements: { 
      Mathematics: 70, 
      English: 50, 
      "Physical Sciences": 50,
      "Minimum Average": 65
    },
    description: "Study computing and software development. Required for careers in software engineering, data science, and IT.",
    notes: "Wits requires 70% in Math and 60% in Physical Science. UCT requires 70% Math."
  },
  {
    career: "BCom Accounting",
    requirements: { 
      Mathematics: 50, 
      English: 50,
      "Minimum Average": 60
    },
    description: "Leads to CA(SA) qualification. Study accounting, economics, and business management.",
    notes: "Some universities accept Maths Literacy at 60%+ for non-CA streams"
  },
  {
    career: "BA Social Sciences",
    requirements: { 
      English: 60,
      "Minimum Average": 55 
    },
    description: "Study politics, sociology, psychology. Flexible degree for various careers.",
    notes: "History/Geography recommended but not required"
  }
];

export default function CareerSuggester() {
  const [query, setQuery] = useState('');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [matchedCareers, setMatchedCareers] = useState([]);

  const filteredSubjects = KNOWN_SUBJECTS.filter(
    subject => subject.toLowerCase().includes(query.toLowerCase()) &&
      !selectedSubjects.find(s => s.subject === subject)
  );

  const handleAddSubject = (subject) => {
    setSelectedSubjects([...selectedSubjects, { subject, mark: '' }]);
    setQuery('');
  };

  const handleMarkChange = (index, value) => {
    const updated = [...selectedSubjects];
    updated[index].mark = value >= 0 && value <= 100 ? value : '';
    setSelectedSubjects(updated);
  };

  const calculateAverage = () => {
    const validMarks = selectedSubjects.filter(s => s.mark !== '').map(s => Number(s.mark));
    return validMarks.length ? 
      (validMarks.reduce((sum, mark) => sum + mark, 0) / validMarks.length).toFixed(1) : 
      'N/A';
  };

  const suggestCareers = () => {
    const userMarks = {};
    selectedSubjects.forEach(({ subject, mark }) => {
      if (mark !== '') userMarks[subject.toLowerCase()] = Number(mark);
    });

    const userAverage = calculateAverage();
    
    const matched = CAREER_DATA.filter(career => {
      const meetsSubjects = Object.entries(career.requirements)
        .filter(([reqSubj]) => !reqSubj.includes("Average"))
        .every(([reqSubj, reqMark]) => 
          userMarks[reqSubj.toLowerCase()] >= reqMark
        );
      
      const meetsAverage = userAverage !== 'N/A' && 
                         Number(userAverage) >= career.requirements["Minimum Average"];
      
      return meetsSubjects && meetsAverage;
    });

    setMatchedCareers(matched);
  };

  const removeSubject = (index) => {
    setSelectedSubjects(selectedSubjects.filter((_, i) => i !== index));
  };

  return (
    <div style={{
      maxWidth: '800px',
      margin: '20px auto',
      fontFamily: 'Arial',
      position: 'relative'
    }}>
      {/* White overlay card */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        padding: '30px',
        marginBottom: '30px'
      }}>
        <h2 style={{ 
          color: '#2c3e50', 
          textAlign: 'center',
          marginBottom: '25px',
          fontSize: '28px'
        }}>
          South African Career Suggester
        </h2>
        
        {/* Search Section */}
        <div style={{ marginBottom: '25px' }}>
          <input
            type="text"
            placeholder="Search for a subject (e.g. Mathematics)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ 
              width: '100%', 
              padding: '12px 15px',
              borderRadius: '6px',
              border: '1px solid #e0e0e0',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          />

          {query && filteredSubjects.length > 0 && (
            <div style={{
              position: 'absolute',
              width: 'calc(100% - 60px)',
              maxHeight: '250px',
              overflowY: 'auto',
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '0 0 6px 6px',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              zIndex: 100
            }}>
              {filteredSubjects.map((subject) => (
                <div 
                  key={subject}
                  onClick={() => handleAddSubject(subject)}
                  style={{
                    padding: '12px 15px',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'background-color 0.2s',
                    ':hover': {
                      backgroundColor: '#f8f8f8'
                    }
                  }}
                >
                  {subject}
                  <span style={{
                    color: '#3498db',
                    fontWeight: 'bold',
                    fontSize: '14px'
                  }}>
                    Add
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected Subjects Card */}
        <div style={{
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px',
          border: '1px solid #eee'
        }}>
          <h4 style={{ 
            marginTop: '0',
            marginBottom: '15px',
            color: '#34495e'
          }}>
            Selected Subjects
          </h4>
          
          {selectedSubjects.length > 0 ? (
            <div style={{ display: 'grid', gap: '12px' }}>
              {selectedSubjects.map((s, index) => (
                <div 
                  key={index} 
                  style={{ 
                    display: 'flex', 
                    gap: '10px',
                    alignItems: 'center'
                  }}
                >
                  <div style={{
                    backgroundColor: '#e3f2fd',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    flex: '1',
                    minWidth: '150px',
                    fontWeight: '500'
                  }}>
                    {s.subject}
                  </div>
                  <input
                    type="number"
                    placeholder="Mark %"
                    value={s.mark}
                    onChange={(e) => handleMarkChange(index, e.target.value)}
                    style={{ 
                      width: '80px',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      textAlign: 'center'
                    }}
                    min="0"
                    max="100"
                  />
                  <button 
                    onClick={() => removeSubject(index)}
                    style={{
                      background: 'none',
                      color: '#e74c3c',
                      border: 'none',
                      padding: '8px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                    title="Remove subject"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ 
              color: '#95a5a6',
              textAlign: 'center',
              padding: '10px'
            }}>
              No subjects added yet
            </p>
          )}
        </div>

        {/* Stats and Action Card */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f0f7ff',
          padding: '15px 20px',
          borderRadius: '8px',
          marginBottom: '25px'
        }}>
          <div>
            <div style={{ fontSize: '14px', color: '#7f8c8d' }}>Average Mark</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2c3e50' }}>
              {calculateAverage()}%
            </div>
          </div>
          <div>
            <div style={{ fontSize: '14px', color: '#7f8c8d' }}>Subjects Added</div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2c3e50' }}>
              {selectedSubjects.filter(s => s.mark !== '').length}
            </div>
          </div>
          <button 
            onClick={suggestCareers}
            disabled={selectedSubjects.filter(s => s.mark !== '').length === 0}
            style={{ 
              padding: '12px 24px',
              background: '#27ae60',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '16px',
              cursor: 'pointer',
              fontWeight: 'bold',
              transition: 'background-color 0.2s',
              ':disabled': {
                backgroundColor: '#95a5a6',
                cursor: 'not-allowed'
              }
            }}
          >
            Suggest Careers
          </button>
        </div>
      </div>

      {/* Results Section */}
      {matchedCareers.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          padding: '30px'
        }}>
          <h3 style={{ 
            marginTop: '0',
            marginBottom: '20px',
            color: '#2c3e50'
          }}>
            Matching Careers ({matchedCareers.length})
          </h3>
          
          <div style={{ display: 'grid', gap: '20px' }}>
            {matchedCareers.map((career) => (
              <div 
                key={career.career}
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '20px',
                  backgroundColor: '#fff'
                }}
              >
                <h4 style={{ 
                  marginTop: '0',
                  marginBottom: '10px',
                  color: '#2980b9',
                  fontSize: '20px'
                }}>
                  {career.career}
                </h4>
                <p style={{ marginBottom: '15px' }}>{career.description}</p>
                
                <div style={{ marginBottom: '15px' }}>
                  <strong style={{ display: 'block', marginBottom: '8px' }}>Requirements:</strong>
                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: '10px'
                  }}>
                    {Object.entries(career.requirements).map(([subject, mark]) => (
                      <div key={subject} style={{
                        backgroundColor: '#f8f9fa',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}>
                        <span style={{ color: '#7f8c8d' }}>{subject}: </span>
                        <strong>{mark}%</strong>
                      </div>
                    ))}
                  </div>
                </div>
                
                {career.notes && (
                  <div style={{ 
                    backgroundColor: '#fffde7',
                    padding: '12px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    borderLeft: '4px solid #fbc02d'
                  }}>
                    <strong style={{ color: '#f57c00' }}>Note: </strong>
                    {career.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}